import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { TextArea } from '@/components/forms/TextArea'
import { Checkbox } from '@/components/forms/Checkbox'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { reseedAgentConfig } from '@/reducers/voiceAgents/voiceAgents.actions'

// Org-1-only modal: paste the full agent_prompt_configs.config JSON to replace
// ("reseed") this agent's stored config — the UI counterpart of the reseed SQL
// scripts. The BE requires the JSON's own agent_id to match this agent.
//
// Two shapes. By default the active generation is refreshed in place and its
// Studio edits keep applying. "Store as a new generation" files the paste as
// the next generation instead: the current generation's edits are kept against
// it and stop applying, reversible from Generations. config_version always
// moves — it is an audit counter, not a choice.
const ReseedConfigModal = ({ show, onClose }) => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const applyLoading = useSelector((state) => state.voiceAgents.loading?.reseedConfig)

  const [jsonText, setJsonText] = useState('')
  const [applyError, setApplyError] = useState(null)
  const [appliedResult, setAppliedResult] = useState(null)
  // Opt-in: store the paste as the NEXT generation instead of overwriting the
  // active one. Default off, because a new generation retires every existing
  // Studio edit — never something a routine reseed should do by accident.
  const [newGeneration, setNewGeneration] = useState(false)
  // New generations only: switch the agent to the generation just created, or
  // leave it staged for review and activate it later from Generations.
  const [activate, setActivate] = useState(true)

  // Reset local state whenever the modal closes so a stale paste/result never
  // carries into the next session (same convention as ImportOverridesModal).
  useEffect(() => {
    if (!show) {
      setJsonText('')
      setApplyError(null)
      setAppliedResult(null)
      setNewGeneration(false)
      setActivate(true)
    }
  }, [show])

  // Client-side parse state: null while the field is empty, { config } when
  // usable, { error } otherwise. Mirrors the Create Agent wizard's validation,
  // plus the this-agent match the BE re-checks server-side.
  const jsonState = useMemo(() => {
    const raw = jsonText.trim()
    if (isEmpty(raw)) {
      return null
    }
    let config
    try {
      config = JSON.parse(raw)
    } catch {
      return { error: 'Not valid JSON.' }
    }
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
      return { error: 'The config must be a JSON object.' }
    }
    if (typeof config.agent_id !== 'string' || isEmpty(config.agent_id)) {
      return { error: 'The config JSON must carry an agent_id field.' }
    }
    if (agentId && config.agent_id !== agentId) {
      return {
        error: `The pasted config is for "${config.agent_id}", but this agent is "${agentId}".`,
      }
    }
    return { config }
  }, [jsonText, agentId])

  const onApply = async () => {
    if (!jsonState?.config || appliedResult) {
      return
    }
    setApplyError(null)
    const result = await dispatch(
      reseedAgentConfig(agentId, jsonState.config, { newGeneration, activate })
    )
    if (result?.ok) {
      setAppliedResult(result)
      return
    }
    if (result?.error) {
      setApplyError(result.error)
    }
  }

  return (
    <BasicModal
      title={agentId ? `Reseed config — ${agentId}` : 'Reseed config'}
      show={show}
      onClose={onClose}
      size="large"
      scrolling
      footer={
        <div className="voice-agents__import-modal-footer">
          <Button onClick={onClose} data-testid="reseed-cancel">
            {appliedResult ? 'Close' : 'Cancel'}
          </Button>
          <Button
            primary
            disabled={!jsonState?.config || applyLoading || Boolean(appliedResult)}
            loading={applyLoading}
            onClick={onApply}
            data-testid="reseed-apply"
          >
            {appliedResult ? 'Reseeded' : 'Reseed config'}
          </Button>
        </div>
      }
    >
      <p className="voice-agents__reseed-hint">
        Paste the full config JSON exactly as stored in <code>agent_prompt_configs.config</code>.
        This <strong>replaces the entire stored config</strong>. No override is ever deleted — where
        they end up depends on the options below.
      </p>
      <TextArea
        className="voice-agents__reseed-textarea"
        aria-label="Config JSON"
        value={jsonText}
        rows={16}
        placeholder={`{ "agent_id": "${agentId ?? 'customer/agent_name'}", "nodes": [...], ... }`}
        onChange={(event) => setJsonText(event.target.value)}
        disabled={applyLoading || Boolean(appliedResult)}
        data-testid="reseed-json-input"
      />
      <Checkbox
        label="Store as a new generation"
        className="voice-agents__reseed-bump"
        checked={newGeneration}
        onChange={(event, data) => setNewGeneration(data.checked)}
        disabled={applyLoading || Boolean(appliedResult)}
        data-testid="reseed-new-generation-checkbox"
      />
      <p className="voice-agents__reseed-bump-hint">
        {newGeneration ? (
          <>
            Saved as the <strong>next generation</strong>. The current generation&apos;s Studio
            edits are kept against it and stop applying — switch back from Generations to bring them
            back.
          </>
        ) : (
          <>The active generation is refreshed; its Studio edits keep applying.</>
        )}
      </p>
      <Checkbox
        label="Make it the active generation"
        className="voice-agents__reseed-bump"
        checked={newGeneration && activate}
        onChange={(event, data) => setActivate(data.checked)}
        disabled={!newGeneration || applyLoading || Boolean(appliedResult)}
        data-testid="reseed-activate-checkbox"
      />
      <p className="voice-agents__reseed-bump-hint">
        {newGeneration && !activate ? (
          <>
            Stored but <strong>not switched to</strong> — the agent keeps running its current
            generation until you activate this one from Generations.
          </>
        ) : (
          <>Only applies when storing a new generation.</>
        )}
      </p>
      {jsonState?.error && (
        <Banner critical header="Invalid config JSON" data-testid="reseed-json-error">
          <p>{jsonState.error}</p>
        </Banner>
      )}
      {applyError && (
        <Banner critical header="Reseed failed" data-testid="reseed-apply-error">
          <p>{applyError}</p>
        </Banner>
      )}
      {appliedResult && (
        <Banner success header="Reseeded" data-testid="reseed-applied-banner">
          <p>
            {appliedResult.new_generation
              ? `Stored as generation ${appliedResult.generation}. Active generation: ${appliedResult.active_generation}.`
              : `Active generation ${appliedResult.active_generation} refreshed (config version ${appliedResult.config_version}).`}
          </p>
        </Banner>
      )}
    </BasicModal>
  )
}

export default ReseedConfigModal
